import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import Grid from '@mui/material/Grid'
import LinearProgress from '@mui/material/LinearProgress'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'

import TaskAltIcon from '@mui/icons-material/TaskAlt'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import TimerIcon from '@mui/icons-material/Timer'
import VerifiedIcon from '@mui/icons-material/Verified'
import GroupIcon from '@mui/icons-material/Group'
import UpdateDisabledIcon from '@mui/icons-material/UpdateDisabled'
import PsychologyIcon from '@mui/icons-material/Psychology'
import BalanceIcon from '@mui/icons-material/Balance'
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart'

type MetricCardProps = {
  icon: ReactNode
  iconBg: string
  label: string
  value: string
  chip: { label: string; color: string; bg: string }
  progress?: number
  progressColor?: string
  subtitle?: string
}

function MetricCard({ icon, iconBg, label, value, chip, progress, progressColor, subtitle }: MetricCardProps) {
  return (
    <Paper
      elevation={0}
      sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider', '&:hover': { boxShadow: 3 }, transition: 'box-shadow 0.2s' }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: iconBg, color: chip.color, display: 'flex' }}>{icon}</Box>
        <Chip
          label={chip.label}
          size="small"
          sx={{ fontWeight: 700, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.05em', bgcolor: chip.bg, color: chip.color }}
        />
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mb: 0.5 }}>
        {label}
      </Typography>
      <Typography variant="h3" sx={{ fontWeight: 800 }}>
        {value}
      </Typography>
      {progress !== undefined && (
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{
            mt: 2,
            height: 4,
            borderRadius: 2,
            bgcolor: 'grey.200',
            '& .MuiLinearProgress-bar': { bgcolor: progressColor || 'primary.main', borderRadius: 2 },
          }}
        />
      )}
      {subtitle && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
          {subtitle}
        </Typography>
      )}
    </Paper>
  )
}

type BiasCardProps = { icon: ReactNode; title: string; description: string; action?: string }

function BiasCard({ icon, title, description, action }: BiasCardProps) {
  return (
    <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
        <Box sx={{ color: 'primary.main' }}>{icon}</Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          {title}
        </Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
        {description}
      </Typography>
      {action && (
        <Box sx={{ mt: 2, p: 1.5, borderRadius: 2, bgcolor: 'primary.50', border: '1px solid', borderColor: 'primary.100' }}>
          <Typography variant="caption" sx={{ fontWeight: 700, color: 'primary.dark' }}>
            {action}
          </Typography>
        </Box>
      )}
    </Paper>
  )
}

export default function AITransparencyPage() {
  const { t } = useTranslation()

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1200, mx: 'auto' }}>
      {/* Hero */}
      <Box sx={{ mb: 5 }}>
        <Typography variant="h3" component="h1" sx={{ fontWeight: 800, letterSpacing: '-0.02em', mb: 1.5 }}>
          {t('common:aiTransparency.title')}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 600, lineHeight: 1.7 }}>
          {t('common:aiTransparency.subtitle')}
        </Typography>
      </Box>

      <Grid container spacing={4}>
        {/* Left: Performance */}
        <Grid item xs={12} lg={8}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              {t('common:aiTransparency.performanceHeading')}
            </Typography>
            <MonitorHeartIcon color="primary" />
          </Box>

          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6}>
              <MetricCard
                icon={<TaskAltIcon />}
                iconBg="rgba(126, 87, 194, 0.08)"
                label={t('common:aiTransparency.answerRelevanceLabel')}
                value="78%"
                chip={{ label: t('common:aiTransparency.answerRelevanceChip'), color: '#2e7d32', bg: '#e8f5e9' }}
                progress={78}
                progressColor="#7e57c2"
                subtitle={t('common:aiTransparency.answerRelevanceSubtitle')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <MetricCard
                icon={<WarningAmberIcon />}
                iconBg="rgba(237, 108, 2, 0.08)"
                label={t('common:aiTransparency.hallucinationRateLabel')}
                value="~15%"
                chip={{ label: t('common:aiTransparency.hallucinationRateChip'), color: '#e65100', bg: '#fff3e0' }}
                subtitle={t('common:aiTransparency.hallucinationRateSubtitle')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <MetricCard
                icon={<TimerIcon />}
                iconBg="rgba(25, 118, 210, 0.08)"
                label={t('common:aiTransparency.avgResponseTimeLabel')}
                value="< 15s"
                chip={{ label: t('common:aiTransparency.avgResponseTimeChip'), color: '#1565c0', bg: '#e3f2fd' }}
                progress={65}
                progressColor="#1976d2"
                subtitle={t('common:aiTransparency.avgResponseTimeSubtitle')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <MetricCard
                icon={<VerifiedIcon />}
                iconBg="rgba(46, 125, 50, 0.08)"
                label={t('common:aiTransparency.sourceFaithfulnessLabel')}
                value="82%"
                chip={{ label: t('common:aiTransparency.sourceFaithfulnessChip'), color: '#2e7d32', bg: '#e8f5e9' }}
                progress={82}
                progressColor="#43a047"
                subtitle={t('common:aiTransparency.sourceFaithfulnessSubtitle')}
              />
            </Grid>
          </Grid>

          {/* Analysis summary */}
          <Paper elevation={0} sx={{ p: 4, borderRadius: 4, bgcolor: 'grey.50' }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
              {t('common:aiTransparency.reliabilityHeading')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>
              {t('common:aiTransparency.reliabilityBody')}
            </Typography>
          </Paper>
        </Grid>

        {/* Right: Biases */}
        <Grid item xs={12} lg={4}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              {t('common:aiTransparency.biasesHeading')}
            </Typography>
            <BalanceIcon sx={{ color: 'text.secondary' }} />
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
            <BiasCard
              icon={<GroupIcon />}
              title={t('common:aiTransparency.biasTrainingTitle')}
              description={t('common:aiTransparency.biasTrainingDescription')}
              action={t('common:aiTransparency.biasTrainingAction')}
            />
            <BiasCard
              icon={<PsychologyIcon />}
              title={t('common:aiTransparency.biasConfirmationTitle')}
              description={t('common:aiTransparency.biasConfirmationDescription')}
              action={t('common:aiTransparency.biasConfirmationAction')}
            />
            <BiasCard
              icon={<UpdateDisabledIcon />}
              title={t('common:aiTransparency.biasTemporalTitle')}
              description={t('common:aiTransparency.biasTemporalDescription')}
              action={t('common:aiTransparency.biasTemporalAction')}
            />
          </Box>
        </Grid>
      </Grid>
    </Box>
  )
}
